// MACHINERY V2 (Stroje) — readiness banner ribbon (M2.9).
//
// Spec: MACHINERY_V2_IMPLEMENTATION_TICKET.md §8 M2.9. Reference impl:
// design_handoff_machinery_v3/machinery-readiness.jsx (`ReadinessBanner`) +
// README.md §8.
//
// MAX ONE BANNER, GOLD OEM OUTRANKS AI-BLUE (locked). That rule is NOT
// re-implemented here: this component renders `presentation.banner`, which is
// `bannerTier()` from midasReadiness.ts — a single-value union
// ("oem-gold" | "ai-blue" | null). Two banners are structurally impossible:
// there is one value, one <div>, and no array anywhere in the chain. Do NOT
// add a second banner element, and do NOT let a host render this twice for
// the same model.
//
// NO CTA (locked). The ribbon is a pure, non-interactive status ribbon — no
// link, no click handler, no navigation. Every CTA lives in ReadinessPopupV2.
//
// Fanout-only models (docs but no direct source, capability not proven) get
// NO banner — `bannerTier` returns null and this renders nothing. Their lit
// MIDAS dot in ReadinessDots is the whole signal, which is the locked
// "controller-fanout => dot, no banner" rule.
//
// HOST PLACEMENT: render as the card's FIRST child, outside the padded body,
// so the ribbon bleeds edge to edge under the card's own border radius
// (the artboard's `margin: -12px -14px 0` on a padded card). The component
// itself carries no negative margins — that geometry belongs to the host card
// (M2.2 fleet card / M2.8 catalog card).
//
// The banner hexes are PINNED cross-palette constants (RD), not palette
// tokens: OEM gold and MIDAS blue must read identically under both palettes.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { MachineryV2ReadinessPresentation, RD } from "./readinessPresentation";

export interface ReadinessBannerProps {
  presentation: MachineryV2ReadinessPresentation;
  /** OEM partner display name — gold banner only. */
  oemPartnerName?: string | null;
}

const ReadinessBanner: React.FC<ReadinessBannerProps> = ({
  presentation: p,
  oemPartnerName,
}) => {
  const { translate } = useTranslation("myMachinery");

  // ONE read of the ONE tier value. No re-derivation, no precedence logic.
  if (!p.banner) return null;
  const gold = p.banner === "oem-gold";

  const label = gold
    ? translate("myMachinery:v2.readiness.bannerOem", {
        oem: oemPartnerName || translate("myMachinery:theManufacturer"),
      })
    : translate("myMachinery:v2.readiness.bannerAiReady");

  return (
    <div
      style={{
        padding: "5px 14px",
        background: gold ? RD.goldBg : RD.blueBg,
        borderBottom: "1px solid " + (gold ? RD.goldLine : RD.blueLine),
        display: "flex",
        alignItems: "center",
        gap: 6,
        minWidth: 0,
        fontFamily: "var(--font-mono)",
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: gold ? RD.goldText : RD.blueText,
      }}
    >
      <span
        aria-hidden
        style={{ color: gold ? RD.goldStar : RD.blueSpark, fontSize: 11, flexShrink: 0 }}
      >
        {gold ? "★" : "✦"}
      </span>
      <span
        style={{
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
    </div>
  );
};

export default ReadinessBanner;
